import React, { useState } from 'react';
import cogoToast from 'cogo-toast';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';

import { getCatalogSnapshotArtifact } from '../../api/catalogs';
import { SNAPSHOT_VIEW_MAX_GZIP_BYTES, downloadTextFile, openJsonInTab } from './catalogArtifactFiles';
import './CatalogSnapshotManager.scss';

const CatalogSnapshotManager = ({ brandKey, BrandIntegration_id, entries, total, isLoading, isPulling, pullLabel, onPull }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [busyKeys, setBusyKeys] = useState(new Set());

  const fetchSnapshot = async (entry, mode) => {
    const busyKey = `${entry.fingerprint}-${mode}`;
    setBusyKeys(current => new Set(current).add(busyKey));
    try {
      const artifact = await getCatalogSnapshotArtifact(brandKey, BrandIntegration_id, entry.fingerprint);
      if (mode === 'download') return downloadTextFile(`${brandKey}-snapshot-${entry.fingerprint}.json`, artifact.content);
      openJsonInTab(artifact.content);
    } catch (artifactError) {
      cogoToast.error(artifactError, { hideAfter: 10 });
    } finally {
      setBusyKeys(current => {
        const next = new Set(current);
        next.delete(busyKey);
        return next;
      });
    }
  };

  return (
    <div className='catalogs-snapshot-manager'>
      <div className='catalogs-snapshot-manager-header'>
        <div className='catalogs-snapshot-manager-heading'>
          <h2>Snapshots</h2>
          <div className='catalogs-snapshot-manager-intro'>{snapshotSummary({ entries, total, isLoading })}</div>
        </div>
        <div className='catalogs-snapshot-manager-actions'>
          {entries.length > 0 && (
            <button
              aria-expanded={isExpanded}
              className='catalogs-button ghost compact'
              onClick={() => setIsExpanded(expanded => !expanded)}
            >
              {isExpanded ? 'Hide snapshots' : 'Show snapshots'}
            </button>
          )}
          <button className='catalogs-button secondary compact' disabled={isPulling} onClick={onPull}>
            {isPulling ? <><FontAwesomeIcon className='catalogs-loading-icon' icon={faSpinner} spin />{pullLabel}</> : 'Pull latest snapshot'}
          </button>
        </div>
      </div>
      {isExpanded && (
        <div className='catalogs-snapshot-manager-list'>
          {entries.map(entry => (
            <div className='catalogs-snapshot-manager-row' key={entry.fingerprint}>
              <div className='catalogs-snapshot-manager-name'>
                <b>{formatStamp(entry.generatedAt || entry.lastModifiedAt)}</b>
                {entry.isLive && <span className='catalogs-snapshot-manager-live'>Live</span>}
              </div>
              <span className='catalogs-snapshot-manager-meta'>{snapshotMeta(entry)}</span>
              <div className='catalogs-snapshot-manager-row-actions'>
                {!(entry.bytes > SNAPSHOT_VIEW_MAX_GZIP_BYTES) && (
                  <button
                    className='catalogs-button ghost compact'
                    disabled={busyKeys.has(`${entry.fingerprint}-view`)}
                    onClick={() => fetchSnapshot(entry, 'view')}
                  >
                    {busyKeys.has(`${entry.fingerprint}-view`) ? 'Loading' : 'View'}
                  </button>
                )}
                <button
                  className='catalogs-button ghost compact'
                  disabled={busyKeys.has(`${entry.fingerprint}-download`)}
                  onClick={() => fetchSnapshot(entry, 'download')}
                >
                  {busyKeys.has(`${entry.fingerprint}-download`) ? 'Loading' : 'Download'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const snapshotSummary = ({ entries, total, isLoading }) => {
  if (isLoading) return 'Loading snapshots…';
  if (!entries.length) return 'No snapshots stored for this integration yet.';
  const latest = entries[0];
  const facts = [`Latest: ${formatStamp(latest.generatedAt || latest.lastModifiedAt)}`];
  if (latest.productCount != null) facts.push(`${Number(latest.productCount).toLocaleString()} products`);
  facts.push(`${Number(total || entries.length).toLocaleString()} stored`);
  return facts.join(' · ');
};

const snapshotMeta = entry => {
  const facts = [];
  if (entry.productCount != null) facts.push(`${Number(entry.productCount).toLocaleString()} products`);
  if (entry.variantCount != null) facts.push(`${Number(entry.variantCount).toLocaleString()} variants`);
  if (entry.recentRunCount) facts.push(`${entry.recentRunCount} recent ${entry.recentRunCount === 1 ? 'run' : 'runs'}`);
  facts.push(entry.fingerprint.slice(0, 8));
  return facts.join(' · ');
};

const formatStamp = value => (value ? moment(value).format('MMM D, h:mm A') : '—');

export default CatalogSnapshotManager;
